// apps/provider/src/services/api/billingRules.js
// Domain module for billing rule CRUD operations

/**
 * Creates billing rule API methods.
 * Bound to ApiClient instance via Object.assign in index.js.
 *
 * @returns {Object} Billing rule methods
 */
const _inFlightBillingRules = new Map();

export function createBillingRulesMethods() {
  return {
    /**
     * Get billing rules for an organization with optional filters.
     * Concurrent calls with identical params share one in-flight promise.
     * @param {Object} filters
     * @param {string} filters.organization_id - Required
     * @param {string} [filters.category] - Filter by rule category
     * @param {string} [filters.payer_category] - Filter by payer scope
     * @param {string} [filters.clinical_specialty] - Filter by specialty
     * @param {boolean} [filters.is_active] - Filter by active status
     * @param {string} [filters.source] - Filter by source (system/custom)
     * @returns {Promise<import('@prism-ehr/shared/types/models/billingRules').BillingRule[]>}
     */
    getBillingRules(filters = {}) {
      const params = new URLSearchParams();
      if (filters.organization_id) params.set('organization_id', filters.organization_id);
      if (filters.category) params.set('category', filters.category);
      if (filters.payer_category) params.set('payer_category', filters.payer_category);
      if (filters.clinical_specialty) params.set('clinical_specialty', filters.clinical_specialty);
      if (filters.care_setting) params.set('care_setting', filters.care_setting);
      if (filters.is_active !== undefined) params.set('is_active', String(filters.is_active));
      if (filters.source) params.set('source', filters.source);

      const key = params.toString();
      if (_inFlightBillingRules.has(key)) return _inFlightBillingRules.get(key);

      const promise = this.request(`/billing-rules?${key}`, { method: 'GET' })
        .finally(() => _inFlightBillingRules.delete(key));

      _inFlightBillingRules.set(key, promise);
      return promise;
    },

    /**
     * Create a new billing rule.
     * @param {Object} data - Rule data
     * @returns {Promise<import('@prism-ehr/shared/types/models/billingRules').BillingRule>}
     */
    createBillingRule(data) {
      return this.request('/billing-rules', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Update a billing rule. System rules only allow enforcement_level and is_active changes.
     * @param {string} ruleId
     * @param {Object} data - Fields to update
     * @returns {Promise<import('@prism-ehr/shared/types/models/billingRules').BillingRule>}
     */
    updateBillingRule(ruleId, data) {
      return this.request(`/billing-rules/${ruleId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    /**
     * Delete a billing rule. System rules cannot be deleted.
     * @param {string} ruleId
     * @returns {Promise<{success: boolean}>}
     */
    deleteBillingRule(ruleId) {
      return this.request(`/billing-rules/${ruleId}`, { method: 'DELETE' });
    },

    /**
     * Seed default billing rules for an organization.
     * Idempotent — skips if system rules already exist.
     * @param {string} organizationId
     * @returns {Promise<{seeded_count: number, skipped_count: number}>}
     */
    seedBillingRules(organizationId) {
      return this.request('/billing-rules/seed', {
        method: 'POST',
        body: JSON.stringify({ organization_id: organizationId }),
      });
    },

    /**
     * Fetch lightweight billing rule context for calendar alerts.
     * v2 contract: rules + per-case SQL aggregates (visit counts, milestone
     * note position, last encounter) + raw POC rows + case fields. The legacy
     * encounters/clinical_notes keys arrive as empty arrays (stale-tab bridge).
     * @param {Object} params
     * @param {string} params.case_ids - Comma-separated case IDs
     * @param {string} params.organization_id
     * @param {string} [params.tz] - IANA timezone for visit-date grouping (browser local)
     * @returns {Promise<{rules: Array, case_aggregates: Array, plans_of_care: Array, cases: Array}>}
     *  case_aggregates rows: { case_id, total_visits, current_visit_number,
     *  last_progress_note_date, last_progress_note_visit, last_encounter_date,
     *  last_signed_progress_note_date, last_signed_progress_note_visit }.
     *  The last_signed_* pair (any-note vs signed-only milestone qualifier) is
     *  read by useCalendarAlerts only when settings.documentation.pn_due_signed_only
     *  is on, selected by key presence so an endpoint without them degrades to
     *  the any-note columns.
     */
    getCalendarBillingContext({ case_ids, organization_id, tz }) {
      return this.request('/calendar-billing-context', {
        method: 'POST',
        body: JSON.stringify({ case_ids, organization_id, tz }),
      });
    },
  };
}
